import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Globe2 } from 'lucide-react';
import { api, type SubscriptionCountries } from '@/lib/api';
import { getWebApp, haptic } from '@/lib/telegram';
import { countryName, regionName } from '@/lib/geo';
import { Flag } from '@/components/ui/flag';
import { IconChip, SectionLabel } from '@/components/ui/calm';
import { Skeleton, SkeletonCard, SkeletonLine, SkeletonPage, SkeletonRows } from '@/components/ui/skeleton';

/**
 * Страны в подписке (заявка #169): человек убирает страны, которые ему не нужны, и
 * VPN-приложение после обновления подписки их больше не показывает. Храним убранные:
 * новая страна у поставщика появится сама. Хотя бы одна страна остаётся всегда.
 */
export const Route = createFileRoute('/countries/$subscriptionId')({
  component: CountriesRoute,
});

/** Черновик принадлежит одной подписке: при переходе на другую экран собирается заново. */
function CountriesRoute() {
  const { subscriptionId } = Route.useParams();
  return <CountriesPage key={subscriptionId} subscriptionId={subscriptionId} />;
}

function CountriesPage({ subscriptionId }: { subscriptionId: string }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const queryKey = ['sub-countries', subscriptionId];
  const { data, isPending, refetch } = useQuery({
    queryKey,
    queryFn: () => api.subscriptionCountries(subscriptionId),
    staleTime: 60 * 1000,
  });

  const [hidden, setHidden] = useState<Set<string> | null>(null);
  // Черновик заводим из ответа один раз: перезапрос не должен стирать то, что человек
  // уже отметил, но ещё не сохранил.
  useEffect(() => {
    if (data && hidden === null) setHidden(new Set(data.hidden));
  }, [data, hidden]);

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    tg.BackButton.show();
    const onBack = () => navigate({ to: '/me' });
    tg.BackButton.onClick(onBack);
    return () => tg.BackButton.offClick(onBack);
  }, [navigate]);

  // Замок на повторный тап по MainButton: `showProgress` её не блокирует.
  const busyRef = useRef(false);
  const save = useMutation({
    mutationFn: (codes: string[]) => api.saveSubscriptionCountries(subscriptionId, codes),
    onSettled: () => {
      busyRef.current = false;
    },
    onSuccess: (fresh: SubscriptionCountries) => {
      qc.setQueryData(queryKey, fresh);
      setHidden(new Set(fresh.hidden));
      haptic('success');
      toast.success(t('Countries saved'), { description: t('Countries saved hint') });
    },
    onError: () => {
      haptic('error');
      toast.error(t('Countries save failed'));
      // Отказ мог быть не случайным (владелец выключил выбор, список поменялся):
      // перечитываем, и экран покажет актуальное состояние, а не ту же кнопку.
      void qc.invalidateQueries({ queryKey });
    },
  });
  const { mutate: saveCountries, isPending: saving } = save;

  const locked = !data?.enabled;
  const dirty =
    !!data && !!hidden &&
    (hidden.size !== data.hidden.length || data.hidden.some((code) => !hidden.has(code)));

  // Сохранение — нативной кнопкой Telegram, как на экранах оплаты: своя кнопка внизу
  // уходила бы под нижнюю навигацию мини-аппа.
  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.MainButton || !data || !hidden || locked || data.countries.length === 0) {
      tg?.MainButton?.hide();
      return;
    }
    tg.MainButton.setText(saving ? t('Saving') : t('Save'));
    tg.MainButton.show();
    // Сначала прогресс, потом доступность: `hideProgress` в SDK сам включает кнопку
    // обратно, и после сохранения она горела бы активной без изменений.
    if (saving) tg.MainButton.showProgress();
    else tg.MainButton.hideProgress();
    if (dirty && !saving) tg.MainButton.enable();
    else tg.MainButton.disable();
    const onMain = () => {
      if (!dirty || busyRef.current) return;
      busyRef.current = true;
      saveCountries([...hidden]);
    };
    tg.MainButton.onClick(onMain);
    return () => {
      tg.MainButton.offClick(onMain);
      tg.MainButton.hideProgress();
    };
  }, [data, hidden, locked, dirty, saving, saveCountries, t]);

  // Уходя с экрана, кнопку убираем: следующий экран её не ждёт.
  useEffect(() => () => getWebApp()?.MainButton?.hide(), []);

  const byRegion = useMemo(() => {
    const out = new Map<string, SubscriptionCountries['countries']>();
    for (const c of data?.countries ?? []) {
      out.set(c.region, [...(out.get(c.region) ?? []), c]);
    }
    return [...out.entries()];
  }, [data]);

  if (isPending || (data && hidden === null)) return <CountriesSkeleton />;
  // Только «данных нет»: упавший фоновый перезапрос при уже загруженном списке не должен
  // подменять редактор экраном ошибки.
  if (!data || !hidden) {
    return (
      <div className="p-4 pb-24">
        <div className="calm-card rounded-[24px] p-6 text-center space-y-3">
          <p className="text-sm text-[#c2c4ca]">{t('Countries load failed')}</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="min-h-11 px-5 rounded-xl bg-white/[0.08] text-sm font-semibold text-foreground active:scale-[0.98] transition-transform"
          >
            {t('Retry')}
          </button>
        </div>
      </div>
    );
  }

  const total = data.countries.length;
  // Выбор выключен владельцем: в приложение приходят все страны, так и показываем.
  // Сам выбор не стёрт и вернётся, когда владелец включит его обратно.
  const view = locked ? new Set<string>() : hidden;
  const shown = total - view.size;

  const toggle = (code: string) => {
    if (locked) return;
    const next = new Set(hidden);
    if (next.has(code)) next.delete(code);
    else if (shown > 1) next.add(code);
    else {
      haptic('warning');
      toast(t('Countries keep one'));
      return;
    }
    haptic('light');
    setHidden(next);
  };

  return (
    <div className="p-4 pb-[calc(env(safe-area-inset-bottom)+110px)] space-y-5">
      <div className="relative overflow-hidden rounded-[28px] calm-card-hero p-6">
        <div className="absolute -top-24 -right-20 w-56 h-56 rounded-full bg-primary blur-[80px] opacity-[0.18] pointer-events-none" />
        <div className="relative z-10 flex items-start gap-4">
          <IconChip icon={Globe2} tone="blue" size={52} radius={16} />
          <div className="min-w-0 flex-1">
            <SectionLabel className="mb-1">{t('Countries in subscription')}</SectionLabel>
            <h1 className="text-[26px] font-extrabold tracking-tight leading-tight tabular text-foreground">
              {t('Countries shown of', { shown, total })}
            </h1>
            <p className="text-xs text-[#8a8c93] mt-1.5 leading-snug">
              {locked ? t('Countries locked') : t('Countries explain')}
            </p>
            {/* «🌍 Авто-выбор» в Happ у «Навсегда» это балансировщик по всем серверам:
                страны поставщика внутри него мы не фильтруем и честно об этом пишем. */}
            {!locked && data.section === 'lifetime' && (
              <p className="text-xs text-[#8a8c93] mt-1.5 leading-snug">{t('Countries auto note')}</p>
            )}
          </div>
        </div>
      </div>

      {total === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-sm text-[#8a8c93]">
          {t('Locations unavailable')}
        </div>
      ) : (
        <>
          {!locked && hidden.size > 0 && (
            <button
              type="button"
              onClick={() => {
                haptic('light');
                setHidden(new Set());
              }}
              className="min-h-11 px-4 rounded-xl bg-white/[0.05] text-sm font-semibold text-accent active:scale-[0.98] transition-transform"
            >
              {t('Countries show all')}
            </button>
          )}
          {byRegion.map(([region, items]) => (
            <section key={region} className="calm-card rounded-2xl overflow-hidden">
              <h2 className="px-4 pt-4 pb-2 font-bold tracking-tight text-foreground">
                {regionName(region, t)}
              </h2>
              <ul className="px-2 pb-2">
                {items.map((c) => {
                  const on = !view.has(c.code);
                  return (
                    <li key={c.code}>
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={on}
                        disabled={locked}
                        onClick={() => toggle(c.code)}
                        className="w-full min-h-12 flex items-center gap-3 px-2 py-2 rounded-xl text-left active:bg-white/[0.04] disabled:opacity-100"
                      >
                        <span className="text-xl leading-none shrink-0">
                          <Flag flag={c.flag} code={c.code} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span
                            className={`block text-sm font-semibold leading-tight break-words hyphens-auto ${on ? 'text-foreground' : 'text-[#8a8c93]'}`}
                          >
                            {countryName(c.code, c.name, i18n.language)}
                          </span>
                          {c.servers > 0 && (
                            <span className="block text-[11px] leading-snug text-[#8a8c93]">
                              {t('Countries servers', { count: c.servers })}
                              {c.limited > 0 && ` · ${t('Countries limited', { count: c.limited })}`}
                            </span>
                          )}
                        </span>
                        <span
                          aria-hidden
                          className={`size-6 shrink-0 rounded-md grid place-items-center transition-colors ${
                            on ? 'bg-primary text-white' : 'bg-white/[0.08]'
                          }`}
                        >
                          {on && <Check className="size-4" strokeWidth={3} />}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </>
      )}

    </div>
  );
}

function CountriesSkeleton() {
  return (
    <SkeletonPage className="space-y-5">
      <SkeletonCard className="rounded-[28px] min-h-0 p-6 flex items-start gap-4">
        <Skeleton className="w-[52px] h-[52px] rounded-[16px] shrink-0" />
        <div className="flex-1 min-w-0 space-y-2.5">
          <SkeletonLine size="xs" className="w-28" />
          <SkeletonLine size="hero" className="w-1/2" />
          <SkeletonLine size="sm" className="w-3/4" />
        </div>
      </SkeletonCard>
      {[0, 1].map((i) => (
        <SkeletonCard key={i} className="rounded-2xl min-h-0 p-4 space-y-3">
          <SkeletonLine size="lg" className="w-28" />
          <SkeletonRows count={4} />
        </SkeletonCard>
      ))}
    </SkeletonPage>
  );
}
